import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import {
  startStripeIdentitySession,
  StripeIdentityVerificationFlowStatus,
} from "@src/appV2/Stripe";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { invalidateWorkerRequirements } from "../../../api/invalidateWorkerRequirements";
import { useArchiveDocument } from "../../../api/useArchiveDocument";
import { useUploadDocumentAndGetVerificationSession } from "./useUploadDocumentAndGetVerificationSession";

interface StartStripeVerificationFlowProps {
  personalIdSubtype: string;
}

interface UseStripeIdentityVerificationFlowProps {
  requirementId: string;
  autoUpdateWorkerName?: boolean;
}

export function useStripeIdentityVerificationFlow(props: UseStripeIdentityVerificationFlowProps) {
  const { requirementId, autoUpdateWorkerName } = props;
  const { uploadDocumentAndGetVerificationSession, isLoading } =
    useUploadDocumentAndGetVerificationSession();
  const { showErrorToast } = useToast();
  const [isOpeningStripeVerificationSession, setIsOpeningStripeVerificationSession] =
    useState(false);
  const queryClient = useQueryClient();
  const { mutateAsync: archiveDocument, isLoading: isArchivingDocument } = useArchiveDocument();

  async function startStripeVerificationFlow(props: StartStripeVerificationFlowProps) {
    try {
      const { personalIdSubtype } = props;

      const { documentId, clientSecret, verificationSessionId } =
        await uploadDocumentAndGetVerificationSession({
          personalIdSubtype,
          requirementId,
          autoUpdateWorkerName,
        });

      logEvent(APP_V2_APP_EVENTS.STRIPE_IDENTITY_FLOW_BEGUN);

      setIsOpeningStripeVerificationSession(true);

      const stripeVerificationStatus = await startStripeIdentitySession({
        clientSecret,
        verificationSessionId,
      });

      if (stripeVerificationStatus !== StripeIdentityVerificationFlowStatus.COMPLETED) {
        try {
          await archiveDocument({ id: documentId });
        } catch (error) {
          logError(APP_V2_APP_EVENTS.DELETE_DOCUMENT_AFTER_STRIPE_IDENTITY_FAILURE, { error });
        }
      }

      await invalidateWorkerRequirements(queryClient);

      setIsOpeningStripeVerificationSession(false);

      if (stripeVerificationStatus === StripeIdentityVerificationFlowStatus.FAILED) {
        showErrorToast("Something went wrong. Please try again later!");
      }

      logEvent(APP_V2_APP_EVENTS.STRIPE_IDENTITY_FLOW_COMPLETED, {
        verificationSessionId,
        success: stripeVerificationStatus === StripeIdentityVerificationFlowStatus.COMPLETED,
      });

      return { stripeVerificationStatus };
    } catch (error) {
      logError(APP_V2_APP_EVENTS.STRIPE_IDENTITY_VERIFICATION_SESSION_FAILURE, {
        error,
      });
      showErrorToast("Something went wrong. Please try again later!");
      return { stripeVerificationStatus: StripeIdentityVerificationFlowStatus.FAILED };
    }
  }

  return {
    startStripeVerificationFlow,
    isLoading: isLoading || isArchivingDocument || isOpeningStripeVerificationSession,
  };
}
